import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShieldCheck, Zap } from "lucide-react";
import { useRef } from "react";
import { BOOK } from "../content";
import { useCart } from "../context/CartContext";
import BookCover from "./BookCover";
import Countdown from "./Countdown";

const LINES = ["THE END", "OF THE", "OBVIOUS"];

const Hero = () => {
  const { addToCart } = useCart();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const coverY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const scrollToExcerpts = () => {
    document.getElementById("excerpts")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16">
      <motion.div style={{ y: bgY }} className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 -right-40 w-[42rem] h-[42rem] rounded-full border border-[#D4AF37]/10 animate-spin-slower" style={{ borderStyle: "dashed" }} />
        <div className="absolute -top-24 -right-24 w-[30rem] h-[30rem] rounded-full border border-[#D4AF37]/15" />
        <div className="absolute top-1/3 -left-56 w-[36rem] h-[36rem] rounded-full bg-[#D4AF37]/5 blur-3xl" />
        <p className="absolute bottom-8 left-0 font-display text-[18vw] leading-none text-outline opacity-40 select-none whitespace-nowrap">
          SIGNALS
        </p>
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center w-full">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-mono-alt text-xs uppercase tracking-[0.35em] text-[#D4AF37] mb-8"
            data-testid="hero-eyebrow"
          >
            From the Archives of the Gg Elves
          </motion.p>

          <h1 className="font-display font-black uppercase tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-7xl text-[#F8FAFC]">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`hero-line-${i}`}
                >
                  {i === 2 ? <span className="text-[#D4AF37]">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-6 font-serif-alt italic text-lg sm:text-xl text-amber-200/80 max-w-xl leading-relaxed"
            data-testid="hero-subtitle"
          >
            {BOOK.subtitle} — a {BOOK.pages}-page reader's edition on noticing what everyone else scrolls past.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <div data-testid="hero-price-block">
              <Countdown />
              <div className="mt-3 flex items-baseline gap-3">
                <span className="font-display text-5xl font-bold text-[#F59E0B]" data-testid="hero-sale-price">
                  ${BOOK.price.toFixed(2)}
                </span>
                <span className="text-xl text-[#94A3B8] line-through" data-testid="hero-original-price">
                  ${BOOK.originalPrice.toFixed(2)}
                </span>
                <span className="font-mono-alt text-[10px] uppercase tracking-[0.2em] text-[#052730] bg-[#D4AF37] rounded-full px-2.5 py-1">
                  Save 67%
                </span>
              </div>
              <p className="mt-1 text-xs text-[#94A3B8]">Limited early-reader offer</p>
            </div>

            <div className="flex flex-wrap gap-4">
              <button
                data-testid="hero-add-to-cart-button"
                onClick={addToCart}
                className="group relative rounded-full bg-[#D4AF37] px-8 py-4 font-semibold text-[#052730] transition-all duration-300 hover:bg-[#FCD34D] hover:shadow-[0_0_40px_rgba(212,175,55,0.45)] focus:outline-none focus:ring-2 focus:ring-[#FCD34D] active:scale-[0.97]"
              >
                Add to Cart — ${BOOK.price.toFixed(2)}
              </button>
              <button
                data-testid="hero-read-excerpts-button"
                onClick={scrollToExcerpts}
                className="rounded-full border border-[#D4AF37]/40 px-8 py-4 font-semibold text-[#FCD34D] transition-colors duration-300 hover:bg-[#D4AF37]/10 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              >
                Read Excerpts
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="mt-8 flex flex-wrap gap-6 text-xs text-[#94A3B8]"
          >
            <span className="flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-[#D4AF37]" /> Instant PDF download + email link</span>
            <span className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" /> Secure encrypted checkout</span>
          </motion.div>
        </div>

        <motion.div
          style={{ y: coverY }}
          initial={{ opacity: 0, y: 60, rotate: 2 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 flex justify-center"
        >
          <BookCover className="w-full max-w-xs sm:max-w-sm" />
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={scrollToExcerpts}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors"
        aria-label="Scroll to excerpts"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="block">
          <ArrowDown className="w-5 h-5" />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
